import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Button, Card, CardHeader, CardContent, Link, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import CreateAccountDialog from './CreateAccountDialog';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';
import { useDispatch } from 'react-redux';
import { createAccount, addAccountMembers } from '../redux/ducks/account';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import ActiveChip from './ActiveChip';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.up("lg")]: {
        marginBottom: theme.spacing(4),
    },
}));

const AccountCard = styled(Card, { shouldForwardProp: (prop) => prop !== 'isDeleted' })(({ theme, isDeleted }) => ({
    backgroundColor: isDeleted
        ? theme.palette.action.disabled
        : theme.palette.background.paper,
    width: '100%',
    margin: theme.spacing(2, 0, 1, 2),
    [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(2, 0, 1, 4),
    },
    
}))

const AccountCardProperty = styled(Typography, { shouldForwardProp: (prop) => prop !== 'isDeleted' })(({ theme, isDeleted }) => ({
    color: isDeleted ? theme.palette.text.disabled : theme.palette.text.secondary,
    textDecoration: isDeleted ? 'line-through' : 'none',
    fontStyle: isDeleted ? 'italic' : 'normal',
}))

const AccountAddButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    marginRight: theme.spacing(1),
}))


const ClientAccountList = (data) => {
    const userAccounts = useSelector(state => state.auth.currentUser.accounts);
    const [accounts, setAccounts] = useState([]);
    const [accountsNumber, setAccountsNumber] = useState(0);
    const [openCreateAccountDialog, setOpenCreateAccountDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canListAccounts = hasPermission('listAccounts');
    const canCreateAccount = hasPermission('addAccount');
    const isLocked = useIsLocked(data.client, null);
    const navigate = useNavigate();

    const handleCreateAccount = (accountData, accountUsers) => {
        setIsLoading(true);
        dispatch(createAccount(data.clientId, accountData, (result) => {
            if (result) {
                dispatch(addAccountMembers(result.account._id, accountUsers.map((user) => ({ _id: user._id, role: user.accountRole })), (result) => {
                    if (result) {
                        dispatch(triggerRefresh({ triggerName: "client" }));
                    }
                }));
            }
            setIsLoading(false);
        }));
        setOpenCreateAccountDialog(false);
    };

    const handleCloseCreateAccountDialog = () => {
        setOpenCreateAccountDialog(false);
    };

    const handleOpenCreateAccountDialog = () => {
        setOpenCreateAccountDialog(true);
    };

    useEffect(() => {
        if (canListAccounts) {  
            setAccounts(data.accounts);
        } else {
            setAccounts(data.accounts.filter(account => userAccounts.some(ua => ua.account === account._id)));
        }
    }, [data.accounts, userAccounts, canListAccounts]);

    useEffect(() => {
        setAccountsNumber(accounts?.length);
    }, [accounts]);

    
    return (
        <>
            <StyledCard p={{ xs: 3, md: 4, lg: 5 }}>
                {(canCreateAccount) ? (
                    <CardHeader title={`Accounts (${accountsNumber})`} action={<AccountAddButton variant="outlined" size="small" onClick={handleOpenCreateAccountDialog} disabled={isLocked}>Add</AccountAddButton>} />
                ) : (
                    <CardHeader title={`Accounts (${accountsNumber})`} />
                )}
                {(accountsNumber &&
                    <CardContent>
                        <Grid container spacing={{ xs: 2, md: 3, lg: 4 }}>
                            {accounts.map((account) => (
                                <AccountCard key={account._id} isDeleted={account.isDeleted} xs={12}>
                                    <CardContent p={2}>
                                        <Box display="flex" justifyContent="space-between" mb={1}>
                                            <StyledLink onClick={() => navigate(`/account/${account.slug}`)} variant="subtitle1" fontWeight="medium">{account.name}</StyledLink>
                                            {account.isDeleted && <Chip size="small" label="Deleted" color="error" />}
                                            {!account.isDeleted && <ActiveChip status={account.active ? 'Active' : 'Inactive'} />}
                                        </Box>
                                        {account.website && (
                                            <StyledLink href="{account.website}" target="_blank">{account.website}</StyledLink>
                                        )}
                                        <Box mt={2} fontSize="0.875rem" color="text.secondary">
                                            <AccountCardProperty isDeleted={account.isDeleted} variant="body2">Start Date: {formatDate(account.startDate)}</AccountCardProperty>
                                            <AccountCardProperty isDeleted={account.isDeleted} variant="body2">Last Update: {formatDateTime(account.updatedAt)}</AccountCardProperty>
                                            <AccountCardProperty isDeleted={account.isDeleted} variant="body2">Status: In Progress</AccountCardProperty>
                                            <AccountCardProperty isDeleted={account.isDeleted} variant="body2">Billing Terms: {account.billingTerms.type === 'Parent' ? 'Inherited from Client' : account.billingTerms.type}</AccountCardProperty>
                                            {account.billingTerms.type === 'Retainer' &&
                                                <AccountCardProperty isDeleted={account.isDeleted} variant="body2">{account.billingTerms.hours} Hrs {account.billingTerms.frequency} / {account.billingTerms.overage ? 'Overage Allowed' : 'No Overage'}</AccountCardProperty>
                                            }
                                            {account.billingTerms.type === 'Fixed Price' &&
                                                <AccountCardProperty isDeleted={account.isDeleted} variant="body2">$ {account.billingTerms.charge} / {account.billingTerms.hours} Hrs </AccountCardProperty>
                                            }
                                        </Box>
                                    </CardContent>
                                </AccountCard>
                            ))}
                        </Grid>
                    </CardContent>
                ) || (
                    <CardContent>
                        
                    </CardContent>
                )
            }
            </StyledCard>
            <CreateAccountDialog isLoading={isLoading} open={openCreateAccountDialog} onClose={handleCloseCreateAccountDialog} clientId={data.clientId} onSubmit={handleCreateAccount} users={data.users} />
        </>
    )
}

export default ClientAccountList;